import React, { useState } from 'react';
import resolveAssetUrl from '../utils/assets';
import './css/Quizpoker.css';

const CLUE_SLOTS = 3;

function formatAction(action) {
    if (!action) return '';
    switch (action.type) {
        case 'check': return 'checkt';
        case 'fold':  return 'foldet';
        case 'call':  return `callt ${action.amount}`;
        case 'raise': return `raiset auf ${action.amount}`;
        case 'allin': return `geht All-In ${action.amount > 0 ? `(${action.amount})` : ''}`.trim();
        case 'ante':  return `zahlt Ante ${action.amount}`;
        case 'blind': return `zahlt Blind ${action.amount}`;
        default:      return action.type;
    }
}

// Eigenständige Quizpoker-Bühne (getrennt von Decoder/TeamSheet).
// Team-Karten werden nicht hier gerendert – sie erscheinen im TeamSheet-Drawer.
function FinaleQuizpokerStage({ finale, teams = [], yourTeamId = null, onSubmitAnswer, onBet }) {
    const [answerInput, setAnswerInput] = useState('');
    const [raiseAmount, setRaiseAmount] = useState(0);

    const live = finale?.live || null;
    const hand = finale?.currentHand || null;
    const pot = live?.pot ?? 0;
    const revealedClues = live?.revealedClues ?? 0;
    const solutionRevealed = !!live?.solutionRevealed;
    const phase = live?.phase || null;
    // Pot + Phase-Badge erst anzeigen wenn für Spieler auch etwas sichtbar ist
    const contentVisible = !!live && (!!live.questionVisible || !!live.imageVisible || revealedClues > 0);

    // Side-Pot-Berechnung: für jedes All-In-Team den maximal erreichbaren Topfanteil berechnen.
    // Bleibt sichtbar in betting, showdown und resolved (bis zur Abrechnung).
    const sidePots = (() => {
        if (!live || !['betting', 'showdown', 'resolved'].includes(phase)) return [];
        const perTeamEntries = Object.entries(live.perTeam || {});
        const nonFolded = perTeamEntries.filter(([, t]) => t.status !== 'folded');
        const allInTeams = nonFolded.filter(([, t]) => t.status === 'allin');
        if (!allInTeams.length) return [];
        const result = [];
        for (const [teamId, allinTeam] of allInTeams) {
            const cap = allinTeam.committedTotal || 0;
            if (cap <= 0) continue;
            const eligiblePot = nonFolded.reduce((sum, [, t]) => sum + Math.min(cap, t.committedTotal || 0), 0);
            if (eligiblePot < pot) {
                result.push({ teamId, eligiblePot });
            }
        }
        return result.sort((a, b) => a.eligiblePot - b.eligiblePot);
    })();

    const clues = hand?.clues || [];
    const attachments = hand?.attachments || [];
    const image = attachments.find((a) => (a?.mime || '').startsWith('image/')) || attachments[0] || null;

    // Bild-Platzierung: 'below' (default) | 'right' | 'fullscreen'
    const imagePlacement = live?.imagePlacement || 'below';
    const showImage = !!live?.imageVisible && !!image?.url;

    // Eigene Schätzung — dauerhaft anzeigen solange kein Showdown.
    // answer != null guard verhindert "Deine Schätzung:  m" wenn Serverstate kurz inkonsistent ist.
    const ownQpTeam = live?.perTeam?.[String(yourTeamId)];
    const showOwnAnswer = yourTeamId && ownQpTeam?.answered && ownQpTeam?.answer != null && phase !== 'showdown' && phase !== 'resolved';

    // Wer ist gerade am Zug (für Issue 9)?
    const actingTeamName = () => {
        if (!live?.actingTeamId) return null;
        const t = teams.find((x) => String(x.id) === String(live.actingTeamId));
        return t?.name || `Team ${live.actingTeamId}`;
    };

    const renderOwnControls = () => {
        if (!live || !yourTeamId) return null;
        const team = teams.find((t) => String(t.id) === String(yourTeamId));
        const qpTeam = live?.perTeam?.[String(yourTeamId)];
        if (!team || !qpTeam) return null;

        if (phase === 'answering') {
            if (qpTeam.answered) return null; // Antwort wird im Daueranzeige-Block gezeigt
            // Eingabefeld erst anzeigen wenn die Frage sichtbar ist
            if (!live?.questionVisible) return null;
            const unit = hand?.unit || null;
            return (
                <div className="qp-own-controls">
                    <input
                        type="number"
                        className="qp-own-answer"
                        value={answerInput}
                        onChange={(e) => setAnswerInput(e.target.value)}
                        placeholder={unit ? `in ${unit}` : 'Schätzung eingeben…'}
                    />
                    <button
                        className="btn btn-primary btn-sm"
                        disabled={answerInput === ''}
                        onClick={() => {
                            if (answerInput === '' || !onSubmitAnswer) return;
                            onSubmitAnswer(Number(answerInput));
                            setAnswerInput('');
                        }}
                    >
                        Abgeben
                    </button>
                </div>
            );
        }

        if (phase === 'betting') {
            const isActing = String(live.actingTeamId) === String(team.id) && qpTeam.status === 'in';

            // Nicht am Zug
            if (!isActing) {
                if (qpTeam.status !== 'in') return null;
                const currentActor = actingTeamName();
                // actingTeamId null = Moderator entscheidet → nichts anzeigen
                if (!live.actingTeamId) return null;
                return (
                    <div className="qp-own-status">
                        {currentActor ? `${currentActor} ist am Zug…` : null}
                    </div>
                );
            }

            const cb = live.currentBet || 0;
            const stack = Math.max(0, (team.punkte || 0) - (qpTeam.committedTotal || 0));
            const toCall = Math.max(0, cb - (qpTeam.roundContribution || 0));
            const canCheck = (qpTeam.roundContribution || 0) === cb;
            const minRaise = finale?.config?.minRaise ?? 1;
            const minRaiseTarget = cb + minRaise;
            const maxRaiseTarget = (qpTeam.roundContribution || 0) + stack;
            const canRaise = maxRaiseTarget >= minRaiseTarget;
            const raiseVal = Math.min(Math.max(raiseAmount || minRaiseTarget, minRaiseTarget), maxRaiseTarget);

            return (
                <div className="qp-own-controls qp-own-bet">
                    {canCheck ? (
                        <button className="btn btn-surface btn-sm" onClick={() => onBet && onBet('check')}>Check</button>
                    ) : (
                        <button className="btn btn-surface btn-sm" disabled={toCall <= 0} onClick={() => onBet && onBet('call')}>
                            Call {Math.min(toCall, stack)}
                        </button>
                    )}
                    {canRaise && (
                        <div className="qp-raise">
                            <input
                                type="range"
                                min={minRaiseTarget}
                                max={maxRaiseTarget}
                                value={raiseVal}
                                onChange={(e) => setRaiseAmount(Number(e.target.value))}
                            />
                            <button className="btn btn-primary btn-sm" onClick={() => onBet && onBet('raise', raiseVal)}>
                                Raise {raiseVal}
                            </button>
                        </div>
                    )}
                    {/* Issue 8: All-In goldener Hintergrund */}
                    <button className="btn btn-allin btn-sm" onClick={() => onBet && onBet('allin')}>All-In</button>
                    <button className="btn btn-danger btn-sm" onClick={() => onBet && onBet('fold')}>Fold</button>
                </div>
            );
        }

        return null;
    };

    // Phase-Label für Spieler (im Header)
    const phaseLabel = (() => {
        if (!live) return null;
        switch (phase) {
            case 'answering': return 'Schätzrunde';
            case 'betting': return 'Setzrunde';
            case 'showdown': return 'Showdown';
            case 'resolved': return 'Abgerechnet';
            default: return null;
        }
    })();

    // Letzte Aktion für die Header-Mitte (Setzrunde)
    const lastActionText = (() => {
        if (!live || phase !== 'betting') return null;
        const { lastActedTeamId } = live;
        if (!lastActedTeamId) return null;
        const team = teams.find((t) => String(t.id) === String(lastActedTeamId));
        const teamName = team?.name || `Team ${lastActedTeamId}`;
        const action = live.perTeam?.[String(lastActedTeamId)]?.lastAction;
        if (!action) return null;
        return `${teamName} ${formatAction(action)}`;
    })();

    return (
        <div className="qp-stage">
            {/* Vollbild-Bild-Overlay (über allem anderen) */}
            {showImage && imagePlacement === 'fullscreen' && (
                <div className="qp-image-fullscreen">
                    <img src={resolveAssetUrl(image.url)} alt="" />
                </div>
            )}

            {/* Kopfzeile: Pot links · letzte Aktion Mitte (cyan) · Phase rechts */}
            <div className="qp-stage-header">
                {contentVisible && (
                    <div className="qp-pot">
                        <span className="qp-pot-label">Pot</span>
                        <span className="qp-pot-value">{pot}</span>
                        {sidePots.length > 0 && (
                            <div className="qp-split-pot-list">
                                {sidePots.map(({ teamId, eligiblePot }) => {
                                    const teamName = teams.find((t) => String(t.id) === String(teamId))?.name || `Team ${teamId}`;
                                    return (
                                        <div key={teamId} className="qp-split-pot-item">
                                            <span className="qp-split-pot-name">{teamName} max</span>
                                            <span className="qp-split-pot-gain">{eligiblePot}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                <div className="qp-stage-header-center">
                    {lastActionText && (
                        <span className="qp-last-action">{lastActionText}</span>
                    )}
                </div>

                {contentVisible && phaseLabel && (
                    <div className="qp-stage-header-right">
                        <span className="qp-phase-badge">{phaseLabel}</span>
                    </div>
                )}
            </div>

            <div className="qp-stage-main">
                {/* Splash: immer wenn noch nichts sichtbar (egal ob Hand läuft oder nicht) */}
                {(!live || (!live.questionVisible && !live.imageVisible && revealedClues === 0)) && (
                    <div className="qp-splash">
                        <span className="qp-splash-title">QUIZPOKER</span>
                        <div className="qp-splash-suits" aria-hidden="true">
                            <span className="qp-suit qp-suit-spade">♠</span>
                            <span className="qp-suit qp-suit-heart">♥</span>
                            <span className="qp-suit qp-suit-diamond">♦</span>
                            <span className="qp-suit qp-suit-club">♣</span>
                        </div>
                    </div>
                )}

                {/* Hauptinhalt: Frage + Bild + Hinweise.
                    Bei 'right': linke Spalte (Frage + Hinweise) | rechte Spalte (Bild).
                    Bei 'below' und default: alles untereinander. */}
                {live && (live.questionVisible || live.imageVisible || revealedClues > 0) && (() => {
                    const isRight = showImage && imagePlacement === 'right';
                    const cluesBlock = (revealedClues > 0 || live.questionVisible || live.imageVisible) && (
                        <div className="qp-clues-row">
                            {Array.from({ length: CLUE_SLOTS }).map((_, i) => {
                                const revealed = i < revealedClues;
                                return (
                                    <div className={`qp-clue-slot ${revealed ? 'revealed' : 'covered'}`} key={i}>
                                        {revealed ? (clues[i] || '') : ''}
                                    </div>
                                );
                            })}
                        </div>
                    );
                    return isRight ? (
                        <div className="qp-content-row">
                            {/* Linke Spalte: Frage + Hinweise */}
                            <div className="qp-content-left">
                                {live.questionVisible && hand?.question && (
                                    <div className="qp-question">{hand.question}</div>
                                )}
                                {cluesBlock}
                            </div>
                            {/* Rechte Spalte: Bild */}
                            <div className="qp-image-side">
                                <img src={resolveAssetUrl(image.url)} alt="" />
                            </div>
                        </div>
                    ) : (
                        <>
                            {live.questionVisible && hand?.question && (
                                <div className="qp-question">{hand.question}</div>
                            )}
                            {showImage && imagePlacement === 'below' && (
                                <div className="qp-image">
                                    <img src={resolveAssetUrl(image.url)} alt="" />
                                </div>
                            )}
                            {cluesBlock}
                        </>
                    );
                })()}

                {solutionRevealed && hand?.answerValue != null && (
                    <div className="qp-solution">
                        Lösung: {hand.answerValue}{hand.unit ? ` ${hand.unit}` : ''}
                    </div>
                )}

                {/* Eigene Schätzung dauerhaft anzeigen — außer im Showdown */}
                {showOwnAnswer && (
                    <div className="qp-own-answer-display">
                        Deine Schätzung: <strong>{ownQpTeam.answer}</strong>
                        {hand?.unit && <span className="qp-own-answer-unit"> {hand.unit}</span>}
                    </div>
                )}

                {/* Eigene Spieler-Controls — unter den Hinweisen */}
                {renderOwnControls()}
            </div>
        </div>
    );
}

export default FinaleQuizpokerStage;
